/**
 * Environment Run Types
 *
 * Types for environment build runs — the execution lifecycle
 * of a theatre construct through the environment canvas pipeline.
 */

// ── Run Status ────────────────────────────────────────────────────────────

export type EnvironmentRunStatus =
  | 'QUEUED'
  | 'INITIALISING'
  | 'INGESTING'
  | 'EVALUATING'
  | 'SETTLING'
  | 'CERTIFYING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED';

/** Terminal statuses — runs in these states will not change. */
export const ENV_TERMINAL_STATUSES: EnvironmentRunStatus[] = [
  'COMPLETED',
  'FAILED',
  'CANCELLED',
];

/** Active statuses — runs in these states are still progressing. */
export const ENV_ACTIVE_STATUSES: EnvironmentRunStatus[] = [
  'INITIALISING',
  'INGESTING',
  'EVALUATING',
  'SETTLING',
  'CERTIFYING',
];

// ── Environment Run ──────────────────────────────────────────────────────

export interface EnvironmentRun {
  run_id: string;
  theatre_id: string;
  construct_id: string;
  status: EnvironmentRunStatus;
  progress: number;
  total_steps: number;
  current_stage: string | null;
  modules_active: number;
  modules_total: number;
  artifacts_produced: number;
  brier_score: number | null;
  composite_score: number | null;
  certificate_id: string | null;
  verification_level: string | null;
  receipt_hash: string | null;
  check_results_json: CheckResultsSummary | null;
  evidence_hash: string | null;
  commitment_hash: string | null;
  ground_truth_hash: string | null;
  ground_truth_source: string | null;
  replay_count: number | null;
  replay_verified: boolean | null;
  attestation_id: string | null;
  scoring_module_id: string | null;
  error: string | null;
  build_state_hash: string | null;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  created_at: string;
  updated_at: string;
}

export interface EnvironmentRunListResponse {
  runs: EnvironmentRun[];
  total: number;
  limit: number;
  offset: number;
}

// ── Run Step (individual stage within a run) ─────────────────────────────

export interface RunStep {
  step_id: string;
  run_id: string;
  stage: string;
  module_id: string | null;
  status: 'PENDING' | 'RUNNING' | 'PASSED' | 'FAILED' | 'SKIPPED';
  message: string | null;
  duration_ms: number | null;
  created_at: string;
}

// ── Check Results (EVALUATED runs) ───────────────────────────────────────

export interface CheckResult {
  check_id: string;
  check_type: string;
  status: 'PASSED' | 'FAILED' | 'SKIPPED';
  template_id: string | null;
  source_id: string | null;
  evidence: Record<string, unknown> | null;
  reason: string | null;
}

export interface CheckResultsSummary {
  construct_slug: string;
  total_executed: number;
  total_passed: number;
  total_failed: number;
  total_skipped: number;
  has_critical_failures: boolean;
  checks: CheckResult[];
}

// ── Environment Attestation (CERTIFIED proof artifact) ───────────────────

export interface EnvironmentAttestation {
  attestation_id: string;
  run_id: string;
  construct_id: string;
  construct_version: string;
  commitment_hash: string;
  ground_truth_hash: string;
  evidence_hash: string;
  receipt_hash: string;
  checks_executed: number;
  checks_passed: number;
  checks_failed: number;
  has_critical_failures: boolean;
  composite_score: number;
  brier_score: number | null;
  replay_count: number;
  replay_verified: boolean;
  scorer_version: string;
  attestation_version: string;
  issued_at: string;
  expires_at: string | null;
  revoked_at: string | null;
  revocation_reason: string | null;
  revoked_by: string | null;
  /** True when expires_at is in the past. Historical proof remains valid. */
  is_expired: boolean;
  /** True when neither expired nor revoked — single field for live trust. */
  is_current: boolean;
}

// ── Module Outputs (per-module structured results) ──────────────────────

/**
 * Per-module output from real Environment execution.
 * output_type discriminates the shape of output_json.
 */
export interface EnvironmentRunModuleOutput {
  output_id: string;
  run_id: string;
  module_id: string;
  module_kind: string;
  output_type: 'theatre_evaluation' | 'forge_intake' | 'scenario_pack_evaluation' | 'agent_readiness_evaluation' | 'paradox_scan' | 'calibration' | 'miroshark_simulation_report' | 'miroshark_market_impact' | 'miroshark_narrative_dynamics' | 'llm_run_summary' | 'autoresearch_mutation_report' | 'candidate_batch' | string;
  output_json: TheatreEvaluationOutput | ForgeIntakeOutput | ScenarioPackEvaluationOutput | AgentReadinessEvaluationOutput | ParadoxScanOutput | CalibrationOutput | MiroSharkSimulationReport | MiroSharkMarketImpact | MiroSharkNarrativeDynamics | LlmRunSummary | Record<string, unknown>;
  created_at: string;
}

/** Structured output for theatre_construct_draft modules. */
export interface TheatreEvaluationOutput {
  construct_slug: string;
  brier_score: number | null;
  composite_score: number;
  checks_executed: number;
  checks_passed: number;
  checks_failed: number;
  has_critical_failures: boolean;
  verification_tier: string;
  replay_count: number;
  replay_verified: boolean;
  is_primary: boolean;
}

/** Structured output for proposal_source modules. */
export interface ForgeIntakeOutput {
  feed_id: string;
  source_id: string;
  domain: string;
  ir_version: string;
  status: 'PROCESSED' | 'EMPTY_ENVELOPE' | 'EMPTY_AFTER_SKIPS' | 'VALIDATION_ERROR';
  proposals_received: number;
  proposals_admitted: number;
  proposals_rejected: number;
  proposals_skipped: number;
  dispositions_count: number;
}

/** Structured output for scenario_pack modules. */
export interface ScenarioPackEvaluationOutput {
  template_id: string;
  template_name: string;
  run_mode: string;
  environment_seed: number;
  status: 'COMPLETED' | 'FAILED' | string;
  total_reward: number;
  checkpoints_resolved: number;
  checkpoints_total: number;
  scenario_run_id: string;
}

/** Structured output for agent_actor modules. */
export interface AgentReadinessEvaluationOutput {
  agent_id: string;
  agent_name: string;
  archetype: string;
  tier: number;
  level: number;
  is_alive: boolean;
  death_cause: string | null;
  sanity: number;
  max_sanity: number;
  sanity_ok: boolean;
  deployment_ready: boolean;
  active_deployments: number;
  total_pnl_usd: number;
  win_rate: number;
  trades_count: number;
}

/** Structured output for paradox_scanner modules. */
export interface ParadoxScanFinding {
  finding_type:
    | 'SCORE_DIVERGENCE'
    | 'CRITICAL_FAILURE_ASYMMETRY'
    | 'VERIFICATION_TIER_MISMATCH'
    | 'REPLAY_DIVERGENCE'
    | 'CALIBRATION_THEATRE_DIVERGENCE'
    | 'THEATRE_SCENARIO_OUTCOME_MISMATCH'
    | string;
  severity: 'INFO' | 'WATCH' | 'MATERIAL';
  module_a_id: string;
  module_b_id: string;
  description: string;
  evidence: Record<string, unknown>;
}

export interface ParadoxScanOutput {
  scan_status: 'COMPLETED' | 'NO_SOURCES' | 'INSUFFICIENT_SOURCES' | 'SKIPPED' | 'ERROR';
  scan_mode: string;
  source_modules_scanned: number;
  source_module_ids: string[];
  source_output_types: string[];
  findings_count: number;
  severity_max: 'INFO' | 'WATCH' | 'MATERIAL' | null;
  finding_types: string[];
  findings: ParadoxScanFinding[];
  scanner_version: string;
  skip_reason?: string;
}

/** Structured output for calibrate_tool modules. */
export interface CalibrationMetricComparison {
  metric: string;
  baseline_value: number | null;
  current_value: number | null;
  delta: number | null;
  abs_delta: number | null;
  outcome: 'IMPROVED' | 'REGRESSED' | 'UNCHANGED' | 'NO_DATA';
  direction: 'HIGHER_IS_BETTER' | 'LOWER_IS_BETTER';
}

export interface CalibrationOutput {
  calibration_status: 'COMPLETED' | 'NO_TARGET' | 'NO_BASELINE' | 'UNSUPPORTED_TYPE' | 'ERROR';
  target_module_id: string;
  target_output_type: string;
  baseline_source: string;
  metrics_compared: number;
  primary_metric: string | null;
  primary_outcome: 'IMPROVED' | 'REGRESSED' | 'UNCHANGED' | 'NO_DATA' | null;
  comparisons: CalibrationMetricComparison[];
  improved_count: number;
  regressed_count: number;
  unchanged_count: number;
  calibrator_version: string;
  skip_reason?: string;
}

/** Lightweight agent roster entry for Environment agent_actor binding. */
export interface AgentRosterEntry {
  agent_id: string;
  name: string;
  archetype: string;
  tier: number;
  level: number;
  sanity: number;
  max_sanity: number;
}

// ── Agent Execution Instance (run-scoped participation) ─────────────────

/**
 * Run-scoped record of an agent's participation in an Environment run.
 * Separates persistent Agent identity from per-run execution context.
 */
export interface AgentExecutionInstance {
  id: string;
  agent_id: string;
  run_id: string;
  deployment_id: string | null;
  module_node_id: string;
  status: 'EVALUATING' | 'COMPLETED' | 'FAILED' | 'SKIPPED';
  strategy_profile: string | null;
  instance_config_json: Record<string, unknown> | null;
  started_at: string;
  finished_at: string | null;
  created_at: string;
}

// ── MiroShark outputs (052) ──────────────────────────────────────────────

/** Structured output for miroshark_simulation modules — simulation report. */
export interface MiroSharkSimulationReport {
  simulation_id: string;
  simulation_type: string;
  target_construct: string;
  status: 'COMPLETED' | 'FAILED' | 'PARTIAL';
  duration_ms: number;
  scenarios_evaluated: number;
  key_findings: string[];
  /** #593: reference_checked = the brief's claims (support is not checked); unchecked = model output. */
  findings_status?: 'reference_checked' | 'unchecked';
  /** #593: the model's own findings, kept apart from the checked claims. */
  unchecked_findings?: string[];
  risk_score: number | null;
  confidence: number | null;
  /** #593: risk_score and confidence are the model's own interpretation. */
  interpretation_status?: 'unchecked';
  simulator_version: string;
}

/** Structured output for miroshark market impact analysis. */
export interface MiroSharkMarketImpact {
  simulation_id: string;
  market_scenarios: number;
  price_impact_range: [number, number] | null;
  liquidity_impact: string | null;
  volatility_estimate: number | null;
  dominant_narrative: string | null;
  interpretation_status?: 'unchecked';
}

/** Structured output for miroshark narrative dynamics analysis. */
export interface MiroSharkNarrativeDynamics {
  simulation_id: string;
  narratives_detected: number;
  dominant_narrative: string | null;
  narrative_stability: number | null;
  competing_narratives: string[];
  convergence_likelihood: number | null;
  interpretation_status?: 'unchecked';
}

// ── LLM run summary output (052) ────────────────────────────────────────

/** Structured output for async LLM-generated run summaries. */
export interface LlmRunSummary {
  summary_text: string;
  key_findings: string[];
  risk_assessment: string | null;
  recommended_actions: string[];
  model_id: string;
  generated_at: string;
}

// ── Filters ──────────────────────────────────────────────────────────────

export interface EnvironmentRunFilters {
  theatre_id?: string;
  status?: EnvironmentRunStatus;
  construct_id?: string;
  limit?: number;
  offset?: number;
}
